import { inject, Injectable } from '@angular/core';
import {
  catchError,
  distinctUntilChanged,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
} from 'rxjs';
import { LocalStorageService } from 'app/api/services/local-storage-service';
import { TenantsService } from './tenants.service';

/**
 * Exposes the active tenant's custom brand logo (the one configured on the
 * tenant settings "Information" tab — `TenantHomeUpdateModel.logo`).
 *
 * The sidebar and the mobile top bar both fall back to the default IGNTR mark
 * (flame + wordmark) and only swap in this logo when one is set. The stream is
 * shared so those two consumers don't each fetch the home model, and it
 * re-loads whenever the active tenant changes.
 *
 * Note: this is independent of the Nybolig-specific logo handling, which those
 * components keep doing themselves.
 */
@Injectable({ providedIn: 'root' })
export class TenantBrandingService {
  private readonly storage = inject(LocalStorageService);
  private readonly tenantsService = inject(TenantsService);

  /** URL of the active tenant's custom logo, or null when none is configured. */
  readonly homeLogoUrl$: Observable<string | null> = this.storage
    .getTenantId$()
    .pipe(
      distinctUntilChanged(),
      switchMap((id) =>
        id
          ? this.tenantsService.loadTenantHomePageModel(id).pipe(
              map((model) => model?.logo?.fileUrl || null),
              catchError(() => of(null)),
            )
          : of(null),
      ),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
}
